import type { Editor } from "@tiptap/react";
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReplySignature } from "../../src/mailbox-reply-signature-contract.ts";
import type { PersonalComposer } from "../../src/personal-composer-contract.ts";
import {
  type ComposerLocalBase,
  type ComposerWords,
  composerWords,
} from "./personal-composer-content.ts";
import { type BoxContent, paragraphs, sameBoxText } from "./reply-box-link.ts";
import { ownWords } from "./reply-signature.ts";
import { keepUnsent, readUnsent, type UnsentReply } from "./unsent-reply.ts";

type Set<T> = Dispatch<SetStateAction<T>>;

/** Tiptap callbacks and React renders must observe the same loaded agent-draft lineage. */
export function useReplyLineage(initial: number | null) {
  const [fromDraft, setState] = useState(initial);
  const fromDraftRef = useRef(initial);
  const setFromDraft = (revision: number | null) => {
    fromDraftRef.current = revision;
    setState(revision);
  };
  return { fromDraft, fromDraftRef, setFromDraft, resetFromDraft: setState };
}

export function capturedReplyMatches(words: ComposerWords, snapshot: BoxContent): boolean {
  return sameBoxText(
    {
      format: words.format,
      text: words.plainText,
      html: words.format === "html" ? words.html : paragraphs(words.plainText),
      loaded: words.fromDraft,
      open: false,
    },
    snapshot,
  );
}

export function useKeptReply(input: {
  key: string;
  messageId: string | null;
  stored: UnsentReply | null;
  signature: MutableRefObject<ReplySignature | null>;
  fromDraft: MutableRefObject<number | null>;
  onEdit(words: ComposerWords): ComposerLocalBase | undefined;
}) {
  const [writtenTo, setWrittenTo] = useState(input.stored?.messageId ?? input.messageId);
  const [kept, setKept] = useState(true);
  const keep = (format: "text" | "html", text: string, html: string) => {
    const fromDraft = input.fromDraft.current;
    const base = input.onEdit({ format, plainText: text, html, fromDraft });
    setWrittenTo(input.messageId);
    if (ownWords(text, input.signature.current?.plainText ?? null) === "" && fromDraft === null) {
      setKept(keepUnsent(input.key, null));
      return;
    }
    const previous = readUnsent(input.key);
    const keptBase = base ?? {
      ...(previous?.composerManaged ? { composerManaged: true as const } : {}),
      ...(previous?.composerRevision === undefined
        ? {}
        : { composerRevision: previous.composerRevision }),
      ...(previous?.composerThreadVersion === undefined
        ? {}
        : { composerThreadVersion: previous.composerThreadVersion }),
    };
    setKept(
      keepUnsent(input.key, {
        messageId: input.messageId,
        format,
        text,
        html,
        ...(fromDraft === null ? {} : { fromDraft }),
        ...keptBase,
        at: new Date().toISOString(),
      }),
    );
  };
  return { writtenTo, setWrittenTo, kept, setKept, keep };
}

type Hydration = {
  key: string;
  editor: Editor | null;
  format: "text" | "html";
  signed: MutableRefObject<boolean>;
  setFormat: Set<"text" | "html">;
  setPlain: Set<string>;
  setRich: Set<string>;
  setRichHtml: Set<string>;
  setFromDraft(revision: number | null): void;
  setWrittenTo: Set<string | null>;
  setLoads: Set<number>;
  setKept: Set<boolean>;
};

function storeHydratedReply(
  input: Hydration,
  composer: PersonalComposer,
  words: ComposerWords | null,
): void {
  input.setKept(
    keepUnsent(
      input.key,
      words
        ? {
            messageId: composer.sourceMessageId,
            format: words.format,
            text: words.plainText,
            html: words.html,
            ...(words.fromDraft === null ? {} : { fromDraft: words.fromDraft }),
            composerRevision: composer.revision,
            composerManaged: true,
            ...(composer.sourceThreadVersion === null
              ? {}
              : { composerThreadVersion: composer.sourceThreadVersion }),
            at: composer.updatedAt ?? new Date().toISOString(),
          }
        : null,
    ),
  );
}

function hydrateReplyWords(input: Hydration, words: ComposerWords, freshSignature: boolean): void {
  input.signed.current = !freshSignature;
  input.setFormat(words.format);
  input.setPlain(words.format === "text" ? words.plainText : "");
  input.setRich(words.format === "html" ? words.plainText : "");
  input.setRichHtml(words.html);
  input.setFromDraft(words.fromDraft);
  input.setLoads((count) => count + 1);
}

/** Whole editor hydration, including the saved signature. No boundary is guessed from its words. */
export function useComposerHydration(input: Hydration) {
  const pending = useRef<ComposerWords | null>(null);
  useEffect(() => {
    if (input.editor && pending.current) {
      input.editor.commands.setContent(pending.current.html, { emitUpdate: false });
      pending.current = null;
    }
  }, [input.editor]);
  return (composer: PersonalComposer) => {
    if (composer.emptyReason === "never_saved") return;
    const content = composerWords(composer);
    const words = content ?? { format: input.format, plainText: "", html: "", fromDraft: null };
    hydrateReplyWords(input, words, composer.emptyReason === "sent");
    input.setWrittenTo(composer.sourceMessageId);
    if (input.editor)
      input.editor.commands.setContent(words.format === "html" ? words.html : "", {
        emitUpdate: false,
      });
    else pending.current = words;
    storeHydratedReply(input, composer, content);
  };
}
